import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Flex, FormControl, FormHelperText, FormLabel, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, VStack,
  useColorModeValue, useToast,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import useCreditMemos from '../hooks/useCreditMemos';
import { money } from '../utils/money';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';
import { useConfirm } from './ConfirmProvider';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string | null;
  invoiceNumber: string;
  customerId: string | null;
  balanceDue: number; // retailTotal — already net of earlier credits + write-offs
  isPaid: boolean;
}

// item 1575 (Chad: "our client took it upon their selves to apply our CM-7 credit memo to their
// payment") — apply a customer's credit memo to an already-FINALIZED, unpaid invoice. The
// finalized paper never changes: the credit lowers the balance due (pay link, deposit, AR,
// statements follow), commissions are untouched, and the accountant gets a CREDIT APPLIED row to
// match QuickBooks (Receive Payments → apply credit). A credit that clears the balance settles
// the invoice as paid by credit. Un-apply is a soft reverse.
const ApplyCreditInvoiceModal: React.FC<Props> = ({
  isOpen, onClose, invoiceId, invoiceNumber, customerId, balanceDue, isPaid,
}) => {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const {
    memos, applications, applyToInvoice, unapply,
  } = useCreditMemos();
  const [memoId, setMemoId] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const available = useMemo(
    () => memos.filter((m) => m.customerId === customerId && m.remaining > 0.004),
    [memos, customerId],
  );
  const memo = available.find((m) => m.id === memoId);
  const mine = applications.filter((a) => a.invoiceId === invoiceId && a.appliedAfterFinalize);
  const memoNumber = (id: string) => memos.find((m) => m.id === id)?.number ?? 'Credit memo';

  // default to the first memo + as much as both the memo and the balance allow
  useEffect(() => {
    if (!isOpen) return;
    const first = available[0];
    setMemoId(first?.id ?? '');
    setAmount(first ? Math.min(first.remaining, balanceDue).toFixed(2) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoiceId]);

  const pickMemo = (id: string) => {
    setMemoId(id);
    const m = available.find((x) => x.id === id);
    if (m) setAmount(Math.min(m.remaining, balanceDue).toFixed(2));
  };

  const refreshAll = () => {
    qc.invalidateQueries('invoices');
    qc.invalidateQueries('project');
    qc.invalidateQueries('accountant-adjustments');
  };

  const value = Number(amount);
  const cap = memo ? Math.min(memo.remaining, balanceDue) : 0;
  const valid = !!memo && value > 0 && value <= cap + 0.0001;
  const clears = valid && Math.abs(value - balanceDue) < 0.005;

  const submit = async () => {
    if (!invoiceId || !memo) return;
    setBusy(true);
    try {
      const r = await applyToInvoice({ creditMemoId: memo.id, invoiceId, amount: value });
      toast({
        status: 'success',
        title: `${money(r.applied)} of ${r.memoNumber} applied to ${invoiceNumber}`,
        description: r.settled
          ? 'The credit covers the whole balance, so it is marked paid by credit. The accountant gets a row to apply it in QuickBooks.'
          : `${money(r.balanceDue)} is still owed in cash. The accountant gets a row to apply the credit in QuickBooks.`,
        duration: 9000,
        isClosable: true,
      });
      refreshAll();
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Credit not applied', description: e?.response?.data ?? 'Nothing was changed.' });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (id: string, amt: number, memoNo: string) => {
    const ok = await confirm(`Un-apply ${money(amt)} of ${memoNo}? The credit goes back on the customer's account and ${invoiceNumber} owes it again.`, { confirmLabel: 'Un-apply credit' });
    if (!ok) return;
    try {
      await unapply(id);
      toast({ status: 'success', title: 'Credit un-applied' });
      refreshAll();
    } catch (e: any) {
      toast({ status: 'error', title: 'Un-apply failed', description: e?.response?.data ?? 'Nothing was changed.' });
    }
  };

  const canApply = !isPaid && balanceDue > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Apply credit — ${invoiceNumber}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {!canApply && <Text color={sub}>There&apos;s no balance due on this invoice to apply a credit to.</Text>}
            {canApply && available.length === 0 && (
              <Text color={sub}>This customer has no open credit memos.</Text>
            )}
            {canApply && available.length > 0 && (
              <>
                <Text fontSize="sm" color={sub}>
                  {`Balance due ${money(balanceDue)}. Use this when the customer took their credit off a payment. The invoice itself doesn't change; the credit lowers what they owe.`}
                </Text>
                <FormControl isRequired>
                  <FormLabel>Credit memo</FormLabel>
                  <Select value={memoId} onChange={(e) => pickMemo(e.target.value)}>
                    {available.map((m) => (
                      <option key={m.id} value={m.id}>
                        {`${m.number} — ${money(m.remaining)} left${m.note ? ` · ${m.note}` : ''}`}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Amount to apply</FormLabel>
                  <Input type="number" step="0.01" min={0} max={cap} value={amount} onChange={(e) => setAmount(e.target.value)} />
                  <FormHelperText>
                    {(() => {
                      if (!valid) return `Up to ${money(cap)}.`;
                      if (clears) return 'Covers the whole balance, so the invoice is marked paid by credit.';
                      return `${money(balanceDue - value)} is still owed in cash.`;
                    })()}
                  </FormHelperText>
                </FormControl>
                <Text fontSize="xs" color={sub}>
                  Commissions don&apos;t change. The pay link and Stripe&apos;s copy are adjusted to the new balance.
                </Text>
              </>
            )}

            {mine.length > 0 && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" mb={2}>CREDITS APPLIED TO THIS INVOICE</Text>
                <VStack align="stretch" spacing={2}>
                  {mine.map((a) => (
                    <Flex key={a.id} justify="space-between" align="center">
                      <Box minW={0}>
                        <Text fontWeight="semibold">
                          {money(a.amount)}
                          <Badge ml={2} fontSize="9px" colorScheme="purple">{memoNumber(a.creditMemoId)}</Badge>
                        </Text>
                        <Text fontSize="xs" color={sub}>{SqlDateTimeToDisplay(a.createdDate)}</Text>
                      </Box>
                      <Button size="xs" variant="outline" onClick={() => undo(a.id, a.amount, memoNumber(a.creditMemoId))}>Un-apply</Button>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
          {canApply && available.length > 0 && (
            <Button colorScheme="purple" onClick={submit} isLoading={busy} isDisabled={!valid}>
              {clears ? 'Apply & mark paid' : 'Apply credit'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ApplyCreditInvoiceModal;
